import { useCallback, useEffect, useState } from 'react';

import { FlexAligner } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import {
  ACCESSIBLE_SITES_SOURCE,
  WidgetConfigModalFooter,
  WidgetConfigSiteTabbedShell,
} from '@/components/widgetConfigSiteTabbedShell/index.ts';
import { StyledModalContent } from '@/styles.ts';
import { WidgetConfigModalProps } from '@/types.ts';
import { responsive } from '@/utils';

import { DialogMode, PlotType } from '../types.ts';
import { ChartConfigBody } from './chartModalContent/ChartConfigBody.tsx';
import { ChartTypePicker } from './chartModalContent/ChartTypePicker.tsx';

type ChartModalContentProps = WidgetConfigModalProps & {
  plotType?: PlotType;
  rootId?: string;
};

// This only works for charts that are widgets in a layout. In other cases, use the PlotType-specific configs
export const ChartModalContent = ({
  widgetId,
  onSave,
  onClose,
  plotType,
  onGoBack,
  rootId,
  setModalTitle,
}: ChartModalContentProps) => {
  const { t } = useTranslation(['common', 'chartConfig']);
  const [dialogMode, setDialogMode] = useState<DialogMode>(plotType ?? 'ChartPicker');

  const handleGoBack = useCallback(() => {
    if (dialogMode === 'ChartPicker') {
      onGoBack?.();
      return;
    }
    setDialogMode('ChartPicker');
  }, [dialogMode, onGoBack]);

  const footerOnGoBack = !plotType && onGoBack ? handleGoBack : undefined;

  useEffect(() => {
    setModalTitle?.(t('chartConfig:chartConfigModal.title'));
  }, [setModalTitle, t]);

  if (dialogMode === 'ChartPicker') {
    return (
      <>
        <StyledModalContent>
          <FlexAligner direction='column' gap={responsive.spacing['4xlarge']} fullHeight>
            <ChartTypePicker onClick={setDialogMode} />
          </FlexAligner>
        </StyledModalContent>
        <WidgetConfigModalFooter onClose={onClose} onGoBack={footerOnGoBack} />
      </>
    );
  }

  return (
    <WidgetConfigSiteTabbedShell
      source={ACCESSIBLE_SITES_SOURCE}
      widgetId={widgetId}
      showItemCount={false}
      keepTabPanelsMounted
      onSave={onSave}
      onClose={onClose}
      onGoBack={footerOnGoBack}
      showSave
      renderSitePanel={() => <ChartConfigBody dialogMode={dialogMode} rootId={rootId} />}
    />
  );
};
